import axios from 'axios';
import { restHost } from '../../apiConfig';
import { formatUrl } from '../../shared/utils';
import { documentDebugError, documentDebugLog } from './documentDebug';
import {
  AnalyticUploadLogRequest,
  AnalyticUploadLogResult,
  AnalyticUploadLogStatus,
} from './analyticUploadLogTypes';
import {
  isDocumentFailed,
  normalizeAllowedRoles,
  normalizeDocumentStatus,
} from './documentStatusUtils';

const LOG_PREFIX = '[AnalyticUploadLog]';

const DOCUMENT_CATEGORY_PATTERNS: ReadonlyArray<{
  category: string;
  pattern: RegExp;
}> = [
  {
    category: 'meds',
    pattern: /\b(meds?|medication|pharmacy|prescription|rx|dose)\b/i,
  },
  {
    category: 'stats',
    pattern: /\b(stats?|statistic|metrics?|analytics|report)\b/i,
  },
  {
    category: 'staff',
    pattern: /\b(staff|employee|roster|caregiver|nurse)\b/i,
  },
];

export const buildDocumentS3Key = (
  documentId: string,
  filename: string,
): string => {
  const id = documentId.trim();
  const name = filename.trim();
  if (!id || !name) {
    return '';
  }

  return `raw/${id}/${name}`;
};

export const detectDocumentCategory = (
  filename: string,
): string | undefined => {
  const trimmed = filename.trim();
  if (!trimmed) {
    return undefined;
  }

  for (const { category, pattern } of DOCUMENT_CATEGORY_PATTERNS) {
    if (pattern.test(trimmed)) {
      return category;
    }
  }

  return undefined;
};

export const mapAnalyticUploadLogStatus = (
  documentStatus?: string,
): AnalyticUploadLogStatus =>
  documentStatus && isDocumentFailed(normalizeDocumentStatus(documentStatus))
    ? 'failed'
    : 'completed';

export const formatAccessPermission = (
  allowedRoles?: string[] | string | Record<string, string[]>,
  filename?: string,
): string => {
  if (allowedRoles == null) {
    return '';
  }

  if (typeof allowedRoles === 'string') {
    const trimmed = allowedRoles.trim();
    if (!trimmed) {
      return '';
    }

    const normalized = normalizeAllowedRoles(trimmed, filename);
    if (normalized?.length) {
      return normalized.join(',');
    }

    return trimmed;
  }

  const normalized = normalizeAllowedRoles(allowedRoles, filename);
  return normalized?.length ? normalized.join(',') : '';
};

export const logAnalyticUpload = async (
  customerId: number,
  token: string,
  payload: AnalyticUploadLogRequest,
): Promise<AnalyticUploadLogResult> => {
  if (!restHost) {
    documentDebugError(
      LOG_PREFIX,
      'REST host is not configured. Set REACT_APP_REST_HOST.',
    );
    return 'rejected';
  }

  if (!token) {
    documentDebugError(LOG_PREFIX, 'Missing access token; skipping upload log');
    return 'unavailable';
  }

  if (!customerId) {
    documentDebugError(LOG_PREFIX, 'Missing customer ID; skipping upload log');
    return 'rejected';
  }

  const body: AnalyticUploadLogRequest = {
    action: payload.action,
    file_name: payload.file_name,
    file_s3_key: payload.file_s3_key,
    access_permission: payload.access_permission,
    status: payload.status,
  };

  if (payload.category?.trim()) {
    body.category = payload.category.trim();
  }

  const url = formatUrl(
    restHost,
    `customers/${customerId}/analytic_upload_log`,
  );

  documentDebugLog(LOG_PREFIX, `→ POST ${url}`, {
    action: body.action,
    file_name: body.file_name,
    status: body.status,
    category: body.category,
  });

  try {
    const response = await axios.post(url, body, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    documentDebugLog(LOG_PREFIX, `← POST ${url} [${response.status}]`);
    return 'logged';
  } catch (error) {
    const status = (error as { response?: { status?: number } }).response
      ?.status;
    documentDebugError(LOG_PREFIX, 'Failed to write analytic upload log', {
      action: body.action,
      file_name: body.file_name,
      status,
      error,
    });

    // 4xx means the server understood and refused (validation, duplicate,
    // unknown customer) — the same payload will never succeed, so don't
    // invite retries. 408/429 are transient by definition; everything else
    // (network, 5xx) is worth retrying.
    if (
      status !== undefined &&
      status >= 400 &&
      status < 500 &&
      status !== 408 &&
      status !== 429
    ) {
      return 'rejected';
    }
    return 'unavailable';
  }
};
